import React, { useId, useMemo, useState } from 'react';
import { format } from 'date-fns';

export interface ServeTrendPoint {
  game_id: string;
  date: string;
  opponent_name: string;
  first_serve_pct: number;
}

interface ServeTrendChartProps {
  trend: ServeTrendPoint[];
}

const WIDTH = 640;
const HEIGHT = 200;
const PADDING = { top: 16, right: 16, bottom: 28, left: 36 };

/**
 * 1st-serve % per game over time. Same small self-contained SVG chart
 * pattern as AttendanceTrendChart.tsx (no chart library in this codebase):
 * 2px line, >=8px markers with a surface ring, recessive hairline
 * gridlines, and a table fallback for accessibility. Single series, so no
 * legend — the heading names what's plotted.
 */
export function ServeTrendChart({ trend }: ServeTrendChartProps) {
  const [hoverIndex, setHoverIndex] = useState<number | null>(null);
  const [showTable, setShowTable] = useState(false);
  const gradientId = useId();

  const plotWidth = WIDTH - PADDING.left - PADDING.right;
  const plotHeight = HEIGHT - PADDING.top - PADDING.bottom;

  const points = useMemo(() => {
    if (trend.length === 0) return [];
    return trend.map((point, index) => {
      const x = trend.length === 1 ? PADDING.left + plotWidth / 2 : PADDING.left + (index / (trend.length - 1)) * plotWidth;
      const y = PADDING.top + plotHeight - (point.first_serve_pct / 100) * plotHeight;
      return { ...point, x, y };
    });
  }, [trend, plotWidth, plotHeight]);

  if (trend.length === 0) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-1">1st Serve % Trend</h3>
        <p className="text-sm text-gray-500">No matches with serve data yet.</p>
      </div>
    );
  }

  const linePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'} ${p.x.toFixed(1)} ${p.y.toFixed(1)}`).join(' ');
  const hovered = hoverIndex !== null ? points[hoverIndex] : null;

  const handlePointerMove = (e: React.PointerEvent<SVGSVGElement>) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const relativeX = ((e.clientX - rect.left) / rect.width) * WIDTH;
    let nearest = 0;
    let nearestDist = Infinity;
    points.forEach((p, i) => {
      const dist = Math.abs(p.x - relativeX);
      if (dist < nearestDist) {
        nearestDist = dist;
        nearest = i;
      }
    });
    setHoverIndex(nearest);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-lg font-semibold text-gray-900">1st Serve % Trend</h3>
          <p className="text-sm text-gray-500 mt-0.5">Last {trend.length} matches</p>
        </div>
        <button
          type="button"
          onClick={() => setShowTable((v) => !v)}
          className="text-sm text-primary hover:underline"
          aria-label={showTable ? 'Show trend as chart' : 'Show trend as table'}
        >
          {showTable ? 'Show chart' : 'View as table'}
        </button>
      </div>

      {showTable ? (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-gray-500 border-b border-gray-200">
                <th className="py-2 pr-4 font-medium">Date</th>
                <th className="py-2 pr-4 font-medium">Opponent</th>
                <th className="py-2 font-medium">1st Serve %</th>
              </tr>
            </thead>
            <tbody>
              {trend.map((point) => (
                <tr key={point.game_id} className="border-b border-gray-100">
                  <td className="py-2 pr-4 text-gray-900">{format(new Date(point.date), 'MMM d, yyyy')}</td>
                  <td className="py-2 pr-4 text-gray-600">{point.opponent_name}</td>
                  <td className="py-2 font-medium text-gray-900">{point.first_serve_pct}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="relative">
          <svg
            viewBox={`0 0 ${WIDTH} ${HEIGHT}`}
            className="w-full h-auto touch-none"
            role="img"
            aria-label={`1st serve percentage line chart across the last ${trend.length} matches`}
            onPointerMove={handlePointerMove}
            onPointerLeave={() => setHoverIndex(null)}
          >
            <defs>
              <linearGradient id={gradientId} x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#2e8b57" stopOpacity={0.12} />
                <stop offset="100%" stopColor="#2e8b57" stopOpacity={0} />
              </linearGradient>
            </defs>

            {[0, 50, 100].map((tick) => {
              const y = PADDING.top + plotHeight - (tick / 100) * plotHeight;
              return (
                <g key={tick}>
                  <line x1={PADDING.left} y1={y} x2={WIDTH - PADDING.right} y2={y} stroke="#e7e5e4" strokeWidth={1} />
                  <text x={PADDING.left - 8} y={y} textAnchor="end" dominantBaseline="middle" className="fill-stone-500" fontSize={10}>
                    {tick}%
                  </text>
                </g>
              );
            })}

            <path
              d={`${linePath} L ${points[points.length - 1].x} ${PADDING.top + plotHeight} L ${points[0].x} ${PADDING.top + plotHeight} Z`}
              fill={`url(#${gradientId})`}
            />
            <path d={linePath} fill="none" stroke="#2e8b57" strokeWidth={2} strokeLinejoin="round" strokeLinecap="round" />

            {hovered && <line x1={hovered.x} y1={PADDING.top} x2={hovered.x} y2={PADDING.top + plotHeight} stroke="#a8a29e" strokeWidth={1} />}

            {points.map((p, i) => {
              const isLast = i === points.length - 1;
              const isHovered = hoverIndex === i;
              return (
                <g key={p.game_id}>
                  <circle cx={p.x} cy={p.y} r={isHovered ? 6 : 4} fill="#2e8b57" stroke="#ffffff" strokeWidth={2} />
                  <circle cx={p.x} cy={p.y} r={12} fill="transparent" />
                  {isLast && (
                    <text x={p.x} y={p.y - 12} textAnchor="middle" className="fill-stone-700" fontSize={11} fontWeight={600}>
                      {p.first_serve_pct}%
                    </text>
                  )}
                </g>
              );
            })}
          </svg>

          {hovered && (
            <div
              className="absolute pointer-events-none bg-gray-900 text-white text-xs rounded px-2 py-1.5 shadow-lg -translate-x-1/2"
              style={{ left: `${(hovered.x / WIDTH) * 100}%`, top: `${(hovered.y / HEIGHT) * 100}%`, marginTop: '-2.5rem' }}
            >
              <div className="font-semibold">{hovered.first_serve_pct}% 1st serve</div>
              <div className="text-gray-300">
                vs {hovered.opponent_name} · {format(new Date(hovered.date), 'MMM d, yyyy')}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default ServeTrendChart;
